import React, { useEffect } from 'react';
import ScanBufferDrawer from './marking/ScanBufferDrawer';
import ScanBufferHandle from './marking/ScanBufferHandle';
import useScanBufferStore from '../store/scanBufferStore';
import { useOperatorGateStore } from '../store/operatorGateStore';
import { Layout as AntLayout, Menu, Button, Typography, Space, Select, Tag, Popover, Divider } from 'antd';
import type { MenuProps } from 'antd';
import {
  TeamOutlined,
  AuditOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  SwapOutlined,
  ProjectOutlined,
  DesktopOutlined,
  ToolOutlined,
  InboxOutlined,
  WifiOutlined,
  ContactsOutlined,
  IdcardOutlined,
  ExperimentOutlined,
  MailOutlined,
  ThunderboltOutlined,
  BarcodeOutlined,
  CloseCircleOutlined,
  CarOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  SafetyCertificateOutlined,
  CalculatorOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useTaskStore from '../store/taskStore';
import useThemeStore from '../store/themeStore';
import useIsMobile from '../hooks/useIsMobile';
import useEdgeSwipeNavigation from '../hooks/useEdgeSwipeNavigation';
import useAutoAddBuffer from '../hooks/useAutoAddBuffer';
import useAutoAddBarcodes from '../hooks/useAutoAddBarcodes';
import useHidScanListener from '../hooks/useHidScanListener';
import useBarcodeScanStore from '../store/barcodeScanStore';
import BarcodeBindModal from './barcode/BarcodeBindModal';
import TaskBell from './TaskBell';
import MobileBottomBar, { MOBILE_BOTTOM_BAR_HEIGHT } from '../layout/MobileBottomBar';

const { Header, Sider, Content } = AntLayout;

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user, logout, hasPermission,
    roles, fetchRoles, startImpersonation, stopImpersonation,
  } = useAuthStore();
  const { startPolling, stopPolling } = useTaskStore();
  const themeMode = useThemeStore((s) => s.mode);
  const isMobile = useIsMobile();
  const startScanBufferPolling = useScanBufferStore((s) => s.startPolling);
  const stopScanBufferPolling = useScanBufferStore((s) => s.stopPolling);
  const startBarcodePolling = useBarcodeScanStore((s) => s.startPolling);
  const stopBarcodePolling = useBarcodeScanStore((s) => s.stopPolling);

  // Edge-swipe между соседними страницами группы меню (только мобилка).
  // На десктопе хук no-op (enabled=false).
  useEdgeSwipeNavigation(isMobile);

  // Автодобавление сканов из буфера в открытый заказ-наряд
  useAutoAddBuffer();
  useAutoAddBarcodes();

  // Глобальный слушатель USB-сканера (marking_buffer §6) — активен только
  // на рабочих местах с функцией usb_scan_listener (workplace_auth §4)
  useHidScanListener();

  const isRealAdmin = user?.real_role === 'admin' || (!user?.impersonating && user?.role === 'admin');
  const canReadMarking = hasPermission('marking:read');
  const canReadBuffer = canReadMarking || hasPermission('orders:read');
  const isMenuPage = location.pathname === '/menu';

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (canReadMarking) {
      startScanBufferPolling();
      return () => stopScanBufferPolling();
    }
  }, [canReadMarking, startScanBufferPolling, stopScanBufferPolling]);

  useEffect(() => {
    if (canReadBuffer) {
      startBarcodePolling();
      return () => stopBarcodePolling();
    }
  }, [canReadBuffer, startBarcodePolling, stopBarcodePolling]);

  useEffect(() => {
    if (isRealAdmin && roles.length === 0) {
      fetchRoles();
    }
  }, [isRealAdmin, roles.length, fetchRoles]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems: MenuProps['items'] = [];

  if (hasPermission('employees:read')) {
    menuItems.push({
      key: 'employees-menu',
      icon: <TeamOutlined />,
      label: 'Сотрудники',
      children: [
        { key: '/employees', label: 'Список сотрудников' },
        {
          key: 'security-menu',
          icon: <SafetyOutlined />,
          label: 'Охрана',
          children: [
            { key: '/timesheet', label: 'Табель' },
          ],
        },
      ],
    });
  }

  if (hasPermission('counterparties:read')) {
    menuItems.push({
      key: 'counterparties-menu',
      icon: <ContactsOutlined />,
      label: 'Контрагенты',
      children: [
        { key: '/counterparties', label: 'Список контрагентов' },
      ],
    });
  }

  if (hasPermission('documents:read')) {
    const docChildren: any[] = [
      { key: '/documents', label: 'Сканированные документы' },
      { key: '/company-orders', label: 'Приказы' },
    ];
    if (hasPermission('documents:write')) {
      docChildren.unshift({ key: '/documents/scan', label: 'Сканировать' });
    }
    menuItems.push({
      key: 'documents-menu',
      icon: <FileTextOutlined />,
      label: 'Документы',
      children: docChildren,
    });
  }

  if (hasPermission('orders:read')) {
    menuItems.push({
      key: 'orders-menu',
      icon: <CarOutlined />,
      label: 'Заказ-наряды',
      children: [
        { key: '/service-orders', label: 'Заказ-наряды' },
        { key: '/oil-change', label: 'Замена масла' },
        ...(hasPermission('orders:write') ? [{ key: '/quick-sale', label: 'Быстрая продажа' }] : []),
        { key: '/vehicles', label: 'Автомобили' },
        { key: '/service-catalog', label: 'Каталог услуг' },
        { key: '/stock', label: 'Склад — остатки' },
        { key: '/stock/receipts', label: 'Приходы на склад' },
        { key: '/1c-service-orders', label: 'Заказ-наряды 1С' },
      ],
    });

    menuItems.push({
      key: '/warehouse-liquids',
      icon: <DatabaseOutlined />,
      label: 'Склад',
    });
  }

  if (hasPermission('individuals:read')) {
    menuItems.push({
      key: '/individuals',
      icon: <IdcardOutlined />,
      label: 'Физлица',
    });
  }

  if (hasPermission('departments:write')) {
    menuItems.push({
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Настройки',
    });
  }

  if (hasPermission('audit:read')) {
    menuItems.push({
      key: '/audit',
      icon: <AuditOutlined />,
      label: 'Журнал действий',
    });
  }

  if (hasPermission('users:read')) {
    menuItems.push({
      key: '/users',
      icon: <UserOutlined />,
      label: 'Пользователи',
    });
  }

  if (hasPermission('users:read')) {
    menuItems.push({
      key: '/roadmap',
      icon: <ProjectOutlined />,
      label: 'Roadmap',
    });
  }

  if (hasPermission('oil:read')) {
    menuItems.push({
      key: 'oil-menu',
      icon: <ExperimentOutlined />,
      label: 'Масло',
      children: [
        { key: '/oil-orders', label: 'Закупка масла' },
        { key: '/product-catalog', label: 'Каталог продуктов' },
        { key: '/oil-pricelists', label: 'Прайс масло' },
      ],
    });
  }

  if (hasPermission('marking:read')) {
    const markingChildren: any[] = [
      { key: '/marking/bulk', label: 'Разливной товар' },
      { key: '/unreceived', label: 'Без оприходования' },
      { key: '/marking/history', label: 'История' },
    ];
    if (hasPermission('direct_posting:use')) {
      markingChildren.push({ key: '/kkt', label: 'Касса' });
    }
    if (hasPermission('marking:write')) {
      markingChildren.unshift({ key: '/marking/scan', label: 'Сканирование' });
    }
    menuItems.push({
      key: 'marking-menu',
      icon: <BarcodeOutlined />,
      label: 'Честный Знак',
      children: markingChildren,
    });
  }

  if (hasPermission('ecology:read')) {
    menuItems.push({
      key: '/ecology',
      icon: <SafetyCertificateOutlined />,
      label: 'Экология',
    });
  }

  if (hasPermission('accounting:read')) {
    menuItems.push({
      key: 'accounting-menu',
      icon: <CalculatorOutlined />,
      label: 'Бухгалтерия',
      children: [
        { key: '/expenses', label: 'Расходы' },
      ],
    });
  }

  if (hasPermission('departments:write')) {
    menuItems.push({
      key: 'work-opt-menu',
      icon: <ThunderboltOutlined />,
      label: 'Оптимизация работ',
      children: [
        { key: '/work-optimization', label: 'Хронометраж' },
        { key: '/work-optimization/operations', label: 'Справочник операций' },
      ],
    });
  }

  if (hasPermission('departments:write')) {
    menuItems.push({
      key: '/workplaces',
      icon: <DesktopOutlined />,
      label: 'Рабочие места',
    });
    menuItems.push({
      key: '/equipment',
      icon: <ToolOutlined />,
      label: 'Оборудование',
    });
    menuItems.push({
      key: '/tools',
      icon: <ToolOutlined />,
      label: 'Инструменты',
    });
    menuItems.push({
      key: '/warehouse',
      icon: <InboxOutlined />,
      label: 'Склад запчастей',
    });
    menuItems.push({
      key: '/equipment-types',
      icon: <ToolOutlined />,
      label: 'Типы оборудования',
    });
    menuItems.push({
      key: '/network',
      icon: <WifiOutlined />,
      label: 'Сеть',
    });
  }

  if (hasPermission('mail:read')) {
    menuItems.push({
      key: '/mail',
      icon: <MailOutlined />,
      label: 'Почта',
    });
  }

  // Material 3 — Navigation Drawer на светлой surface-container,
  // pill-shape активный пункт. Ant Design — оригинальный тёмный sidebar.
  const isMd3 = themeMode === 'md3';
  // M3 Navigation Drawer — surface-container из палитры синего seed (#0061A4)
  const siderBg = isMd3 ? '#ECEDF1' : '#001529';
  const titleColor = isMd3 ? '#001D36' : '#fff';

  const menuNode = (
    <Menu
      theme={isMd3 ? 'light' : 'dark'}
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      style={{ background: siderBg, borderInlineEnd: 'none' }}
      onClick={({ key }) => {
        if (!key.startsWith('/')) return;
        navigate(key);
      }}
    />
  );

  return (
    <AntLayout className="app-shell" style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider width={240} style={{ background: siderBg }}>
          <div style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 12px',
            lineHeight: 0,
          }}>
            <img
              src="/mobil1-logo.webp"
              alt="Mobil 1 Центр"
              style={{
                maxWidth: '90%',
                maxHeight: 28,
                objectFit: 'contain',
                display: 'block',
                margin: 'auto',
              }}
            />
          </div>
          {menuNode}
        </Sider>
      )}
      <AntLayout>
        {!isMobile && (
          <Header style={{
            background: user?.impersonating ? '#fffbe6' : '#fff',
            padding: '0 24px',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            <Space>
              <TaskBell />
              {user?.impersonating && (
                <Tag
                  color="warning"
                  closable
                  closeIcon={<CloseCircleOutlined />}
                  onClose={() => { stopImpersonation(); navigate('/'); }}
                  style={{ marginRight: 0 }}
                >
                  admin &rarr; {user.role}
                </Tag>
              )}
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <div style={{ width: 260 }}>
                    <div style={{ padding: '4px 4px 8px' }}>
                      <Typography.Text strong>
                        {user?.workplace && user?.operator
                          ? user.operator.display_name
                          : (user?.display_name || user?.username)}
                      </Typography.Text>
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Роль: {user?.role}
                        </Typography.Text>
                      </div>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {user?.workplace && user?.operator && (
                        <Button
                          type="text"
                          block
                          style={{ textAlign: 'left' }}
                          icon={<SwapOutlined />}
                          onClick={() => useOperatorGateStore.getState().openManual()}
                        >
                          Сменить оператора
                        </Button>
                      )}
                      <Button
                        type="text"
                        block
                        style={{ textAlign: 'left' }}
                        icon={<SettingOutlined />}
                        onClick={() => navigate('/profile')}
                      >
                        Настройки
                      </Button>
                      {isRealAdmin && !user?.impersonating && (
                        <div style={{ padding: '4px 8px' }}>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Войти как роль:
                          </Typography.Text>
                          <Select
                            placeholder="Выбрать роль..."
                            size="small"
                            style={{ width: '100%', marginTop: 4 }}
                            options={roles.filter(r => r.name !== 'admin').map(r => ({ value: r.id, label: r.name }))}
                            onChange={(roleId) => { startImpersonation(roleId); navigate('/'); }}
                          />
                        </div>
                      )}
                      <Divider style={{ margin: '4px 0' }} />
                      <Button
                        type="text"
                        block
                        danger
                        style={{ textAlign: 'left' }}
                        icon={<LogoutOutlined />}
                        onClick={handleLogout}
                      >
                        Выйти
                      </Button>
                    </div>
                  </div>
                }
              >
                <Button type="text" icon={<UserOutlined />}>
                  {user?.workplace && user?.operator
                    ? user.operator.display_name
                    : (user?.display_name || user?.username)}
                </Button>
              </Popover>
            </Space>
          </Header>
        )}
        <Content style={{
          margin: isMobile ? '12px 8px' : 24,
          paddingBottom: isMobile && !isMenuPage
            ? `calc(${MOBILE_BOTTOM_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`
            : undefined,
        }}>
          <Outlet />
        </Content>
      </AntLayout>
      {canReadBuffer && (
        <>
          {/* Edge-вкладка буфера — только десктоп. На мобиле — compact-variant
              в правом слоте нижнего бара (см. MobileBottomBar ниже). */}
          {!isMobile && <ScanBufferHandle />}
          <ScanBufferDrawer />
          <BarcodeBindModal />
        </>
      )}
      {isMobile && !isMenuPage && (
        <MobileBottomBar
          right={canReadBuffer ? <ScanBufferHandle variant="compact" /> : null}
        />
      )}
    </AntLayout>
  );
};

export default AppLayout;
